import { useEffect, useRef, useState } from 'react';
import InkStroke from '../../../components/base/InkStroke';

const offerings = [
  {
    title: 'Marketing Execution',
    body: 'We scope, organise, and deliver marketing projects your team doesn\'t have the bandwidth for — campaigns, content, websites, CRM, and analytics. We work as a calm extension of your team, keeping things moving without adding overhead.',
  },
  {
    title: 'Operations & Ways of Working',
    body: 'We review and adapt your project needs. We are a team that sustains progress and success with you over time.',
  },
  {
    title: 'UK & Europe Market Entry',
    body: 'We help overseas businesses land well in the UK and European markets — from strategy and positioning to on-the-ground execution across digital, events, and partnerships.',
  },
  {
    title: 'Trusted Specialist Network',
    body: 'When a project needs skills beyond our own, we connect you with freelance professionals from our network — people we know, trust, and would work with ourselves. No lengthy searches, no cold introductions.',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-white/40">
      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <div
          className={`text-center mb-14 md:mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
            What We Do
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom">
            Services designed to compound
          </h2>
          <InkStroke className="mt-6" />
        </div>

        {/* Offerings grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {offerings.map((o, i) => (
            <div
              key={o.title}
              className={`transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${200 + i * 120}ms` }}
            >
              <div className="h-full bg-parchment rounded-lg p-6 md:p-8 border border-amber/10 hover:border-amber/30 transition-colors duration-300">
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <div className="w-8 h-[1.5px] bg-amber" />
                </div>
                <h3 className="font-body text-sm font-semibold text-saddlebrown mb-3">
                  {o.title}
                </h3>
                <p className="font-body text-sm text-muted-umber leading-relaxed">
                  {o.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}